import type { FC } from 'react';

import { useCms } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import {
  Checkbox,
  Grid,
  Heading,
  InputEmail,
  InputNumber,
  InputTelephone,
  InputText,
  RadioButton,
  RadioGroup,
  Text,
} from '@eneco/sparky';
import { UseFormRegister, Control, Controller, FieldErrors } from 'react-hook-form';

import { hintOf } from './hint';
import { stripOuterParagraph } from '../DynamicPriceCard';
import type { FormFields } from './types';
import type { ContactFormFields } from '../contact-form-content.types';

type Props = {
  register: UseFormRegister<FormFields>;
  formFields: ContactFormFields;
  contactDetailsTitle: string | undefined;
  budgetManagerContent: unknown;
  control: Control<FormFields, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<FormFields>;
  canHaveBudgetManager: boolean;
  authorIsBudgetManager?: boolean;
};

const ContactInputs: FC<Props> = ({
  register,
  formFields,
  contactDetailsTitle,
  budgetManagerContent,
  control,
  errors,
  canHaveBudgetManager,
  authorIsBudgetManager,
}) => {
  const { adapter } = useCms();
  const {
    budgetManagerToggleFormField,
    salutationFormField,
    firstNameFormField,
    surnamePrepositionFormField,
    surnameFormField,
    phoneNumberFormField,
    emailAddressFormField,
    customerIdFormField,
  } = formFields;
  return (
    <>
      <Heading size="S" as="h3">
        {contactDetailsTitle}
      </Heading>
      {canHaveBudgetManager && (
        <Checkbox
          {...register('authorIsBudgetManager')}
          label={budgetManagerToggleFormField.label ?? ''}
          hint={hintOf(budgetManagerToggleFormField.hint)}
        />
      )}
      {authorIsBudgetManager && (
        <Text size="BodyM" color="textLowEmphasis">
          {/* Inline on Sitecore; Sanity's Portable Text arrives as one paragraph. */}
          <RichText html={stripOuterParagraph(adapter.toRichText(budgetManagerContent))} />
        </Text>
      )}
      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            label={salutationFormField.label ?? ''}
            hint={hintOf(salutationFormField.hint)}
            value={value}
            onValueChange={onChange}
            error={errors.salutation?.message}>
            {salutationFormField?.options?.map(option => (
              <RadioButton value={option.name ?? ''} key={option.name}>
                {option.label ?? ''}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />
      <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="6" alignY="end">
        <InputText
          {...register('firstName')}
          label={firstNameFormField.label ?? ''}
          error={errors.firstName?.message}
          placeholder={firstNameFormField.placeholder ?? ''}
          hint={hintOf(firstNameFormField.hint)}
        />
        <InputText
          {...register('surnamePreposition')}
          label={surnamePrepositionFormField.label ?? ''}
          error={errors.surnamePreposition?.message}
          placeholder={surnamePrepositionFormField.placeholder ?? ''}
          hint={hintOf(surnamePrepositionFormField.hint)}
          isOptional
        />
        <InputText {...register('surname')} label={surnameFormField.label ?? ''} error={errors.surname?.message} />
      </Grid>
      <Grid columns={{ initial: '1', sm: '2' }} gap="6">
        <InputTelephone
          {...register('phoneNumber')}
          label={phoneNumberFormField.label ?? ''}
          error={errors.phoneNumber?.message}
          autoComplete="tel"
          placeholder={phoneNumberFormField.placeholder ?? ''}
          hint={hintOf(phoneNumberFormField.hint)}
        />

        <InputEmail
          {...register('emailAddress')}
          label={emailAddressFormField.label ?? ''}
          error={errors.emailAddress?.message}
          autoComplete="email"
          placeholder={emailAddressFormField.placeholder ?? ''}
          hint={hintOf(emailAddressFormField.hint)}
        />
      </Grid>
      <InputNumber
        isOptional
        {...register('customerId')}
        label={customerIdFormField.label ?? ''}
        error={errors.customerId?.message}
        formatOptions={{ useGrouping: false }}
        placeholder={customerIdFormField.placeholder ?? ''}
        hint={hintOf(customerIdFormField.hint)}
      />
    </>
  );
};

export default ContactInputs;
